import { useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material'
import { Download } from '@mui/icons-material'
import { fetchLoginLogs, type LoginLogRow } from '../../api/login-logs'
import { formatDatetimeInTimezone } from '../../utils/timezones'
import { useAuthStore } from '../../stores/auth.store'
import { useToastStore } from '../../stores/toast.store'

export function LoginLogs() {
  const timezone = useAuthStore((s) => s.user?.timezone) ?? 'UTC'
  const toast = useToastStore()
  const [logs, setLogs] = useState<LoginLogRow[]>([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)

  const loadLogs = useCallback(async (cursorParam?: string) => {
    setLoading(true)
    try {
      const result = await fetchLoginLogs({ cursor: cursorParam, limit: 50 })
      const newLogs = result.data ?? []
      setLogs((prev) => cursorParam ? [...prev, ...newLogs] : newLogs)
      setHasMore(result.pagination?.has_more ?? false)
      setCursor(result.pagination?.next_cursor ?? null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载登录日志失败')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadMore = useCallback(() => {
    if (cursor) loadLogs(cursor)
  }, [cursor, loadLogs])

  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1'
      const token = useAuthStore.getState().token
      const res = await fetch(`${API_BASE}/settings/login-logs/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'login_logs.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '导出失败')
    } finally {
      setExporting(false)
    }
  }, [toast])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Login Logs</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<Download />} onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button variant="outlined" size="small" onClick={() => loadLogs()} disabled={loading}>
            {logs.length === 0 ? 'Load Logs' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {logs.length === 0 && !loading && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>Click "Load Logs" to view login history</Typography>
        </Paper>
      )}

      {logs.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>User Agent</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Login Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{log.display_name || '-'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{log.user_id}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{log.ip_address ?? '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.user_agent ?? '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.success ? 'Success' : 'Failed'}
                      size="small"
                      color={log.success ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDatetimeInTimezone(log.login_at, timezone)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {hasMore && !loading && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button size="small" onClick={loadMore}>Load More</Button>
        </Box>
      )}
    </Box>
  )
}
